import React, { useState, useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import api from '../../services/api';

interface DashboardStats {
  operatorCount: number;
  locationCount: number;
  chargePointCount: number;
}

interface RecentActivity {
  _id: string;
  type: string;
  action: string;
  description: string;
  createdAt: string;
  user: {
    name: string;
    email: string;
    role: string;
  };
}

const AdminDashboard: React.FC = () => {
  const { t } = useTranslation();
  const [stats, setStats] = useState<DashboardStats>({
    operatorCount: 0,
    locationCount: 0,
    chargePointCount: 0
  });
  const [recentActivities, setRecentActivities] = useState<RecentActivity[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => {
    fetchDashboardData();
  }, []);

  const fetchDashboardData = async () => {
    try {
      setLoading(true);
      const [statsResponse, activitiesResponse] = await Promise.all([
        api.get('/dashboard/stats'),
        api.get('/dashboard/activities')
      ]);
      
      setStats(statsResponse.data);
      setRecentActivities(activitiesResponse.data);
    } catch (error: any) {
      console.error('Error fetching dashboard data:', error);
      setError(error.response?.data?.error || 'Error fetching dashboard data');
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return <div className="flex justify-center items-center h-screen">{t('common.loading')}</div>;
  }

  return (
    <div className="p-6">
      <h2 className="text-2xl font-bold mb-6">{t('dashboard.title')}</h2>

      {error && (
        <div className="bg-red-100 border border-red-400 text-red-700 px-4 py-3 rounded mb-4">
          {error}
        </div>
      )}

      {/* Statistics Cards */}
      <div className="grid grid-cols-1 md:grid-cols-3 gap-6 mb-8">
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm font-medium">{t('dashboard.stats.totalOperators')}</h3>
          <p className="text-3xl font-bold text-gray-900">{stats.operatorCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm font-medium">{t('dashboard.stats.totalLocations')}</h3>
          <p className="text-3xl font-bold text-gray-900">{stats.locationCount}</p>
        </div>
        <div className="bg-white rounded-lg shadow p-6">
          <h3 className="text-gray-500 text-sm font-medium">{t('dashboard.stats.totalChargePoints')}</h3>
          <p className="text-3xl font-bold text-gray-900">{stats.chargePointCount}</p>
        </div>
      </div>

      {/* Recent Activities */}
      <div className="bg-white rounded-lg shadow">
        <div className="px-6 py-4 border-b">
          <h3 className="text-lg font-medium">{t('dashboard.stats.recentActivities')}</h3>
        </div>
        <div className="p-6">
          {recentActivities.length > 0 ? (
            <div className="space-y-4">
              {recentActivities.map((activity) => (
                <div key={activity._id} className="flex items-start space-x-4 p-4 hover:bg-gray-50">
                  <div className="flex-shrink-0">
                    <span className={`inline-flex items-center justify-center h-8 w-8 rounded-full ${
                      activity.action === 'create' ? 'bg-green-100 text-green-500' :
                      activity.action === 'update' ? 'bg-blue-100 text-blue-500' :
                      'bg-red-100 text-red-500'
                    }`}>
                      {activity.type.charAt(0).toUpperCase()}
                    </span>
                  </div>
                  <div className="flex-1 min-w-0">
                    <p className="text-sm text-gray-900 whitespace-pre-wrap">
                      {activity.description}
                    </p>
                    <div className="mt-1 flex items-center text-sm text-gray-500">
                      <span className="font-medium">{activity.user?.name}</span>
                      <span className="mx-1">•</span>
                      <span className="capitalize">{activity.user?.role}</span>
                    </div>
                    <p className="text-xs text-gray-400 mt-1">
                      {new Date(activity.createdAt).toLocaleDateString('en-US', {
                        year: 'numeric',
                        month: 'short',
                        day: 'numeric',
                        hour: '2-digit',
                        minute: '2-digit'
                      })}
                    </p>
                  </div>
                </div>
              ))}
            </div>
          ) : (
            <p className="text-gray-500 text-center">{t('dashboard.stats.noActivities')}</p>
          )}
        </div>
      </div>
    </div>
  );
};

export default AdminDashboard; 